import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import ChannelSelector from "../components/ChannelSelector";
import ClarityScoreChart from "../components/ClarityScoreChart";
import ClarityDistributionChart from "../components/ClarityDistributionChart";
import ChannelClarityTable from "../components/ChannelClarityTable";
// import ClarityInsightCard from "../components/ClarityInsightCard";
// import OverallClarityInsightCard from "../components/OverallClarityInsightCard";
import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';
import EmptyState from '../components/EmptyState';

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  fetchSlackChannels,
  clearCache
} from "../utils/slackDataService";

// Import report service functions
import {
  fetchReport,
  calculateClarityTimeData,
  calculateClarityDistributionDataForChart,
  calculateChannelClarityData
} from "../utils/slackReportService";

import { shouldBypassSlackConnection } from "../utils/config";

const Clarity = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days"
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  // Data states
  const [clarityScoreData, setClarityScoreData] = useState([]);
  const [clarityDistributionData, setClarityDistributionData] = useState([]);
  const [channelClarityData, setChannelClarityData] = useState([]);
  
  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Retrieve user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);
  
  // Check Slack connection and load Slack data
  useEffect(() => {
    const loadSlackData = async () => {
      if (!organization) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // If we should bypass, set slackConnected to true and proceed
        if (shouldBypassSlackConnection()) {
          console.log('Clarity.jsx: Bypassing Slack check, setting slackConnected=true');
          setSlackConnected(true);
          const channels = await fetchSlackChannels(organization);
          setAvailableChannels(channels);
        } else {
          // Check if Slack is connected
          const isConnected = await checkSlackConnection(organization);
          setSlackConnected(isConnected);
          
          if (isConnected) {
            // Load channels
            const channels = await fetchSlackChannels(organization);
            setAvailableChannels(channels);
          } else {
            setError("Slack is not connected. Please connect Slack in the Integrations page.");
          }
        }
      } catch (error) {
        console.error("Error loading Slack data:", error);
        if (!shouldBypassSlackConnection()) {
          setError("Error checking Slack connection. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadSlackData();
  }, [organization]);

  // Load clarity data when slackConnected becomes true
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      loadClarityData(false);
    }
  }, [slackConnected, organization, initialLoadComplete]);
  
  // Load clarity data based on filters
  const loadClarityData = async (isFilterChange = true, channelsOverride = null) => {
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected");
      return;
    }
    
    try {
      if (isFilterChange) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoDataAvailable(false);
      
      // Force clear the cache when loading fresh data with filters
      clearCache();
      
      // Use channelsOverride if provided, otherwise use selectedChannels state
      const channelsToUse = channelsOverride !== null ? channelsOverride : selectedChannels;
      
      console.log("Loading clarity data with filter:", { 
        dateRange, 
        selectedChannels: channelsToUse,
        isFilterChange 
      });
      
      // Ensure we have valid date objects
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      console.log(`Date range: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);
      
      // Get channel names to filter by (since backend uses names as keys)
      const channelNames = channelsToUse.length > 0 
        ? channelsToUse
        : null;
      
      // First, fetch the report data from the database
      const reportResult = await fetchReport(organization, {
        channels: channelNames
      });
      
      // Calculate clarity data from the database report data
      let timeData = [];
      let distributionData = [];
      let channelData = [];
      
      if (reportResult && reportResult.success && reportResult.data) {
        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];
        
        timeData = calculateClarityTimeData(reportResult.data.reportData, startDateStr, endDateStr);
        distributionData = calculateClarityDistributionDataForChart(reportResult.data.reportData, startDateStr, endDateStr);
        channelData = calculateChannelClarityData(reportResult.data.reportData, startDateStr, endDateStr);
        
        // Enrich channel data with is_private property
        try {
          const availableChannels = await fetchSlackChannels(organization);
          const channelMap = new Map(availableChannels.map(ch => [ch.name, ch.is_private]));
          channelData = channelData.map(item => ({
            ...item,
            is_private: channelMap.get(item.channel) || false
          }));
        } catch (error) {
          console.warn("Could not fetch channels for is_private property:", error);
        }
      }
      
      console.log(`Loaded data: ${timeData.length} time points, ${distributionData.length} distribution buckets, ${channelData.length} channels`);
      
      setClarityScoreData(timeData);
      setClarityDistributionData(distributionData);
      setChannelClarityData(channelData);
      
      // Check if we have any real data
      const hasTimeData = timeData.some(item => item.score > 0);
      const hasDistributionData = distributionData.some(item => item.count > 0);
      const hasChannelData = channelData.length > 0;
      
      console.log("Data status:", { hasTimeData, hasDistributionData, hasChannelData });
      
      if (!hasTimeData && !hasDistributionData && !hasChannelData) {
        setNoDataAvailable(true);
      } else {
        setNoDataAvailable(false);
      }
      
    } catch (error) {
      console.error("Error loading clarity data:", error);
      setError("Error loading clarity data. Some data may be incomplete.");
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };
  
  // Handle date range change - stabilized with useCallback
  const handleDateRangeChange = useCallback((newRange) => {
    console.log("Date range changed:", newRange);
    
    // Validate the date range before proceeding
    if (!newRange || !newRange.startDate || !newRange.endDate) {
      console.warn("Invalid date range received:", newRange);
      return;
    }
    
    // Update UI state first
    setClarityScoreData([]);
    setClarityDistributionData([]);
    setChannelClarityData([]);
    setDataLoading(true);
    
    // Update the date range state
    setDateRange(newRange);
  }, []);

  // Load data when dateRange changes (for filter changes)
  useEffect(() => {
    if (dateRange && organization && slackConnected && initialLoadComplete) {
      // Use setTimeout to ensure state is updated before loading data
      setTimeout(() => {
        loadClarityData(true);
      }, 0);
    }
  }, [dateRange, organization, slackConnected, initialLoadComplete]);
  
  // Handle channel selection change
  const handleChannelChange = (channels) => {
    setSelectedChannels(channels);
    loadClarityData(true, channels); // Pass the new channels directly
  };
  
  // Filter channel data based on selection
  const filteredChannelData = selectedChannels.length > 0
    ? channelClarityData.filter(channel => selectedChannels.includes(channel.channel))
    : channelClarityData;
  
  // Show loading indicator for the entire page during initial load
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex h-screen h-dvh overflow-hidden bg-grey-10">
        <Sidebar user={user} organization={organization} organizations={organizations} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-grey-40 border-t-brand rounded-full animate-spin inline-block mb-4"></div>
            <p className="text-grey-60">Loading clarity data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} organizations={organizations} />
      
      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Header and filters */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Clarity</h1>
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <div className="mx-5">
            <EmptyState
              variant="no-connection"
              title="Slack Not Connected"
              description="You need to connect to Slack first to analyze the clarity of your team's communications."
              actionButton={
                <button 
                  onClick={() => navigate('/settings')}
                  className="bg-brand text-white px-6 py-2 rounded-md hover:bg-brand-dark transition-colors font-medium"
                >
                  Go to Settings
                </button>
              }
            />
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-light p-4 rounded-md mx-5 my-4">
            <p className="text-red-strong">{error}</p>
          </div>
        )}
        

        
        {/* Main content - only show if Slack is connected and data is loaded */}
        {slackConnected && (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <ChannelSelector 
                  availableChannels={availableChannels}
                  selectedChannels={selectedChannels}
                  onChange={handleChannelChange}
                />
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={() => window.location.reload()} />
            </div>
            
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
              </div>
            )}
            
            {/* Charts row */}
            <div className="flex gap-4 p-5 pt-0">
              <div className="flex-1">
                <ClarityScoreChart data={clarityScoreData} />
              </div>
              <div style={{ width: '400px' }}>
                <ClarityDistributionChart data={clarityDistributionData} />
              </div>
            </div>
            
            {/* Channel clarity table */}
            <ChannelClarityTable data={filteredChannelData} />
            
            {/* Empty data message when tables/charts are empty */}
            {(filteredChannelData.length === 0 || noDataAvailable) && !dataLoading && (
              <div className="mt-4">
                <EmptyState
                  variant="search"
                  title="No channel data matches your filters"
                  description="Try adjusting your date range or channel selection to see clarity data."
                  actionButton={null}
                />
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Right insights sidebar - COMMENTED OUT FOR NOW */}
      {/*
      <div className="w-[360px] border-l border-grey-20 bg-white overflow-y-auto">
        <div className="p-6">
          <h2 className="text-lg font-medium text-black-0 mb-6">Insights</h2>
          
          <div className="space-y-4">
            {filteredChannelData.slice(0, 5).map((channel, index) => (
              <ClarityInsightCard 
                key={channel.id || index} 
                channel={channel}
                clarityScoreData={clarityScoreData}
                distributionData={clarityDistributionData}
              />
            ))}
            
            {clarityScoreData.length > 0 && (
              <OverallClarityInsightCard 
                scoreData={clarityScoreData}
                distributionData={clarityDistributionData}
              />
            )}
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

export default Clarity; 